import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { ColumnDef } from '@tanstack/react-table';
import { api, ApiClientError, ApiAuthError } from '../lib/api';
import { Button, Input, Checkbox, Badge } from '../atoms';
import { StatCard, DataTable, useToast } from '../organisms';
import { formatDateTime } from '../lib/orgTimezone';

/**
 * YearRoll — /v2/year-roll (super_admin / admin only)
 *
 * Item 14.1 — scope selector + preview + history. Locks the org's current
 * system year and rolls clients + properties + parcels + invoice_terms forward.
 * The operator picks a SCOPE (entire org, or a subset of states / clients),
 * runs a DRY-RUN PREVIEW (real counts of what would roll, from the backend),
 * then confirms the real roll. A live HISTORY table lists prior roll_jobs.
 *
 * All data is real:
 *   GET  /api/workflow/year-roll/scope-options  → from/to years + rollable books
 *   POST /api/workflow/year-roll/preview        → dry-run counts (no mutation)
 *   POST /api/workflow/year-roll                → execute (scoped or org-wide)
 *   GET  /api/roll-jobs                          → prior roll history
 */

interface CurrentUser { id: string; role: string; org_name: string | null; }
interface Org { id: string; current_system_year: number | null; }

interface ScopeOptions {
  fromYear: number | null;
  toYear: number | null;
  states: Array<{ state: string | null; client_count: number }>;
  clients: Array<{ id: string; name: string; state: string | null; rollable: boolean }>;
}

interface RollPreview {
  scoped: boolean;
  fromYear: number | null;
  toYear: number | null;
  clientsToRoll: number;
  propertiesToRoll: number;
  parcelsToRoll: number;
  parcelsToSkip: number;
  invoiceTermsToRoll: number;
  jurisdictionsToRoll: number;
  assessorsToRoll: number;
  collectorsToRoll: number;
}

// The execute endpoint (POST /api/workflow/year-roll) returns camelCase from the
// workflow service (executeYearRoll{Full,Scoped}).
interface YearRollResult {
  rollId: string;
  fromYear: number;
  toYear: number;
  scoped?: boolean;
  clientsRolled: number;
  propsRolled: number;
  parcelsRolled: number;
  parcelsSkipped: number;
  termsRolled: number;
  itemsCreated?: number;
  jursRolled?: number;
  assessorsRolled?: number;
  collectorsRolled?: number;
}

interface RollJob {
  id: string;
  from_year: number;
  to_year: number;
  status: string;
  started_at: string | null;
  completed_at: string | null;
  clients_rolled: number | null;
  properties_rolled: number | null;
  parcels_rolled: number | null;
  parcels_skipped: number | null;
  invoice_terms_rolled: number | null;
  jurisdictions_rolled: number | null;
  assessors_rolled: number | null;
  collectors_rolled: number | null;
}

type ScopeMode = 'org' | 'scoped';

function fmtDate(v: string | null): string {
  return formatDateTime(v);
}

const STATUS_VARIANT: Record<string, 'success' | 'warning' | 'danger' | 'info' | 'neutral'> = {
  completed: 'success',
  in_progress: 'info',
  pending: 'neutral',
  failed: 'danger',
  rolled_back: 'warning',
};

const historyColumns: ColumnDef<RollJob>[] = [
  {
    accessorKey: 'from_year',
    header: 'Roll',
    cell: ({ row }) => (
      <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-sm)' }}>
        {row.original.from_year} → {row.original.to_year}
      </span>
    ),
  },
  {
    accessorKey: 'status',
    header: 'Status',
    cell: ({ getValue }) => {
      const s = String(getValue() ?? '');
      return <Badge variant={STATUS_VARIANT[s] ?? 'neutral'}>{s.replace(/_/g, ' ')}</Badge>;
    },
  },
  { accessorKey: 'clients_rolled', header: 'Clients', cell: ({ getValue }) => Number(getValue() ?? 0).toLocaleString() },
  { accessorKey: 'properties_rolled', header: 'Properties', cell: ({ getValue }) => Number(getValue() ?? 0).toLocaleString() },
  {
    accessorKey: 'parcels_rolled',
    header: 'Parcels',
    cell: ({ row }) => {
      const rolled = Number(row.original.parcels_rolled ?? 0);
      const skipped = Number(row.original.parcels_skipped ?? 0);
      return <span>{rolled.toLocaleString()}{skipped ? ` (${skipped.toLocaleString()} skipped)` : ''}</span>;
    },
  },
  { accessorKey: 'invoice_terms_rolled', header: 'Terms', cell: ({ getValue }) => Number(getValue() ?? 0).toLocaleString() },
  { accessorKey: 'assessors_rolled', header: 'Assessors', cell: ({ getValue }) => Number(getValue() ?? 0).toLocaleString() },
  { accessorKey: 'collectors_rolled', header: 'Collectors', cell: ({ getValue }) => Number(getValue() ?? 0).toLocaleString() },
  { accessorKey: 'started_at', header: 'Started', cell: ({ getValue }) => fmtDate(getValue() as string | null) },
];

export function YearRoll() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [confirmText, setConfirmText] = useState('');
  const [result, setResult] = useState<YearRollResult | null>(null);
  const [mode, setMode] = useState<ScopeMode>('org');
  // §1.8 — scope is EXACTLY ONE: entire org, one state, OR one client. Never a
  // multi-select intersection. A state and a client are mutually exclusive.
  const [selectedState, setSelectedState] = useState<string | null>(null);
  const [selectedClient, setSelectedClient] = useState<string | null>(null);
  const [preview, setPreview] = useState<RollPreview | null>(null);

  const userQuery = useQuery<CurrentUser>({
    queryKey: ['current-user'],
    queryFn: ({ signal }) => api.get<CurrentUser>('/api/current-user', signal),
    retry: (failureCount, error) => {
      if (error instanceof ApiAuthError) return false;
      return failureCount < 2;
    },
  });

  const isStaffAdmin = userQuery.data?.role === 'super_admin' || userQuery.data?.role === 'admin' || userQuery.data?.role === 'administrator';

  const orgQuery = useQuery<Org[]>({
    queryKey: ['organizations'],
    queryFn: ({ signal }) => api.get<Org[]>('/api/organizations', signal),
  });

  const scopeQuery = useQuery<ScopeOptions>({
    queryKey: ['year-roll-scope-options'],
    queryFn: ({ signal }) => api.get<ScopeOptions>('/api/workflow/year-roll/scope-options', signal),
    enabled: isStaffAdmin,
  });

  const historyQuery = useQuery<RollJob[]>({
    queryKey: ['roll-jobs'],
    queryFn: ({ signal }) => api.get<RollJob[]>('/api/roll-jobs?sort=created_at&dir=desc', signal),
    enabled: isStaffAdmin,
  });

  // The scope payload the preview + roll share. Org-wide mode sends an empty
  // body (the historical org-wide roll); scoped mode sends the picked states +
  // clients (Domain 14 granular roll).
  const scopeBody = useMemo(() => {
    if (mode === 'org') return {} as { client_ids?: string[]; states?: string[] };
    if (selectedClient) return { client_ids: [selectedClient] };
    if (selectedState) return { states: [selectedState] };
    return {};
  }, [mode, selectedClient, selectedState]);

  const previewMutation = useMutation<RollPreview, Error, void>({
    mutationFn: () => api.post<RollPreview>('/api/workflow/year-roll/preview', scopeBody),
    onSuccess: (data) => setPreview(data),
    onError: (err) => toast({ variant: 'error', message: errorMessage(err) }),
  });

  const rollMutation = useMutation<YearRollResult, Error, void>({
    // expected_from_year lets the server reject a stale double-submit: if the org's
    // year already advanced (a first roll committed), this submit's shown year no
    // longer matches and the roll is refused 409 instead of double-advancing a cycle.
    mutationFn: () => api.post<YearRollResult>('/api/workflow/year-roll', { ...scopeBody, expected_from_year: fromYear }),
    onSuccess: (data) => {
      toast({ variant: 'success', message: `Year roll complete: ${data.fromYear} to ${data.toYear}.` });
      setResult(data);
      setConfirmText('');
      queryClient.invalidateQueries();  // wipe ALL caches — year/records changed
    },
    onError: (err) => toast({ variant: 'error', message: errorMessage(err) }),
  });

  if (userQuery.isLoading || orgQuery.isLoading) {
    return <p style={{ color: 'var(--color-ink-3)' }}>Loading…</p>;
  }

  // Role gate
  if (userQuery.data && !isStaffAdmin) {
    return (
      <div style={{
        padding: 'var(--sp-5)',
        background: 'var(--color-status-red-bg)',
        border: '1px solid var(--color-status-red)',
        color: 'var(--color-status-red)',
        borderRadius: 'var(--r-lg)',
      }}>
        <h2 style={{ margin: '0 0 var(--sp-2)' }}>Access denied</h2>
        <p style={{ margin: 0 }}>Year roll is restricted to super_admin and admin roles.</p>
      </div>
    );
  }

  const org = orgQuery.data?.[0];
  const scope = scopeQuery.data;
  // Prefer the scope-options year (authoritative for the roll); fall back to the
  // org record, then the calendar year.
  const fromYear = scope?.fromYear ?? org?.current_system_year ?? new Date().getFullYear();
  const toYear = scope?.toYear ?? fromYear + 1;

  const states = scope?.states ?? [];
  const clients = scope?.clients ?? [];
  const rollableClients = clients.filter(c => c.rollable);

  const scopeChosen = mode === 'org' || selectedState != null || selectedClient != null;
  // Scope-aware confirm string so a scoped (per-client / per-state) roll can't be
  // mistaken for a whole-book roll: org mode types `ROLL <year>`; scoped mode must
  // type `ROLL <year> SCOPED`.
  const requiredConfirm = mode === 'scoped' ? `ROLL ${fromYear} SCOPED` : `ROLL ${fromYear}`;
  const confirmMatches = confirmText.trim() === requiredConfirm;

  // §1.8 — single-select with mutual exclusion: choosing a state clears any client
  // (and vice-versa); clicking the chosen one again clears it (re-enabling the rest).
  function toggleState(st: string) {
    setPreview(null);
    setSelectedState(prev => (prev === st ? null : st));
    setSelectedClient(null);
  }
  function toggleClient(id: string) {
    setPreview(null);
    setSelectedClient(prev => (prev === id ? null : id));
    setSelectedState(null);
  }
  function changeMode(next: ScopeMode) {
    setMode(next);
    setPreview(null);
    setConfirmText('');  // required confirm string is scope-aware — clear the stale entry
    if (next === 'org') { setSelectedState(null); setSelectedClient(null); }
  }

  if (result) {
    return <YearRollComplete result={result} onClose={() => navigate('/')} />;
  }

  return (
    <>
      <header style={{ marginBottom: 'var(--sp-5)' }}>
        <h1 style={{
          margin: 0,
          fontSize: 'var(--fs-3xl)',
          fontWeight: 'var(--fw-semibold)',
          letterSpacing: 'var(--ls-tight)',
          color: 'var(--color-ink)',
        }}>Year Roll</h1>
        <p style={{
          margin: 'var(--sp-1) 0 0',
          color: 'var(--color-ink-3)',
          fontSize: 'var(--fs-md)',
        }}>
          Roll the book forward from {fromYear} to {toYear}. Pick a scope, preview the impact, then confirm. This action is irreversible.
        </p>
      </header>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 220px), 1fr))',
        gap: 'var(--sp-4)',
        marginBottom: 'var(--sp-6)',
      }}>
        <StatCard label="From year" value={String(fromYear)} caption="current_system_year" accent="amber" data-testid="yr-from-year" />
        <StatCard label="To year" value={String(toYear)} caption="post-roll" accent="hunter" />
        <StatCard label="Rollable clients" value={String(rollableClients.length)} caption={`${clients.length - rollableClients.length} excluded`} accent="info" />
        <StatCard label="States in book" value={String(states.length)} caption="distinct client states" accent="green" />
      </div>

      {/* Scope selector */}
      <div style={{
        padding: 'var(--sp-5)',
        background: 'var(--color-card)',
        border: '1px solid var(--color-border)',
        borderRadius: 'var(--r-lg)',
        marginBottom: 'var(--sp-5)',
      }}>
        <h2 style={{ margin: '0 0 var(--sp-3)', fontSize: 'var(--fs-lg)', fontWeight: 'var(--fw-semibold)', color: 'var(--color-ink)' }}>Scope</h2>
        <div style={{ display: 'flex', gap: 'var(--sp-5)', marginBottom: mode === 'scoped' ? 'var(--sp-4)' : 0 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)', cursor: 'pointer' }}>
            <input type="radio" name="scope-mode" checked={mode === 'org'} onChange={() => changeMode('org')} />
            <span style={{ color: 'var(--color-ink)' }}>Entire organization</span>
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)', cursor: 'pointer' }}>
            <input type="radio" name="scope-mode" checked={mode === 'scoped'} onChange={() => changeMode('scoped')} />
            <span style={{ color: 'var(--color-ink)' }}>Selected state or client</span>
          </label>
        </div>
        {mode === 'scoped' && (
          <p style={{ margin: '0 0 var(--sp-3)', color: 'var(--color-ink-3)', fontSize: 'var(--fs-sm)' }}>
            Pick exactly one — a single state <em>or</em> a single client. Choosing one greys out the rest.
          </p>
        )}

        {mode === 'scoped' && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 240px), 1fr))', gap: 'var(--sp-5)' }}>
            <div>
              <h3 style={{ margin: '0 0 var(--sp-2)', fontSize: 'var(--fs-sm)', textTransform: 'uppercase', letterSpacing: 'var(--ls-wide)', color: 'var(--color-ink-3)' }}>States</h3>
              {states.length === 0 && <p style={{ color: 'var(--color-ink-3)', fontSize: 'var(--fs-sm)' }}>No states in book.</p>}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)' }}>
                {states.map(s => {
                  const key = s.state ?? '(none)';
                  return (
                    <Checkbox
                      key={key}
                      label={`${key} (${s.client_count})`}
                      checked={s.state != null && selectedState === s.state}
                      disabled={s.state == null || selectedClient != null || (selectedState != null && selectedState !== s.state)}
                      onChange={() => s.state != null && toggleState(s.state)}
                    />
                  );
                })}
              </div>
            </div>
            <div>
              <h3 style={{ margin: '0 0 var(--sp-2)', fontSize: 'var(--fs-sm)', textTransform: 'uppercase', letterSpacing: 'var(--ls-wide)', color: 'var(--color-ink-3)' }}>Clients</h3>
              {rollableClients.length === 0 && <p style={{ color: 'var(--color-ink-3)', fontSize: 'var(--fs-sm)' }}>No rollable clients.</p>}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)', maxHeight: 240, overflowY: 'auto' }}>
                {rollableClients.map(c => (
                  <Checkbox
                    key={c.id}
                    label={`${c.name}${c.state ? ` · ${c.state}` : ''}`}
                    checked={selectedClient === c.id}
                    disabled={selectedState != null || (selectedClient != null && selectedClient !== c.id)}
                    onChange={() => toggleClient(c.id)}
                  />
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Preview */}
      <div style={{
        padding: 'var(--sp-5)',
        background: 'var(--color-card)',
        border: '1px solid var(--color-border)',
        borderRadius: 'var(--r-lg)',
        marginBottom: 'var(--sp-5)',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--sp-3)' }}>
          <h2 style={{ margin: 0, fontSize: 'var(--fs-lg)', fontWeight: 'var(--fw-semibold)', color: 'var(--color-ink)' }}>Preview</h2>
          <Button
            variant="secondary"
            loading={previewMutation.isPending}
            disabled={!scopeChosen || previewMutation.isPending}
            onClick={() => previewMutation.mutate()}
          >
            {preview ? 'Refresh preview' : 'Preview impact'}
          </Button>
        </div>
        {!preview && (
          <p style={{ margin: 0, color: 'var(--color-ink-3)', fontSize: 'var(--fs-md)' }}>
            Run a dry-run to see exactly what would roll — nothing is written until you confirm below.
          </p>
        )}
        {preview && (
          <>
            <p style={{ margin: '0 0 var(--sp-4)', color: 'var(--color-ink-3)', fontSize: 'var(--fs-sm)' }}>
              {preview.scoped ? 'Scoped roll' : 'Org-wide roll'} · {preview.fromYear} → {preview.toYear}
              {preview.scoped ? '' : ' · advances the organization year'}
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 160px), 1fr))', gap: 'var(--sp-4)' }}>
              <StatCard label="Clients" value={String(preview.clientsToRoll)} caption="would roll" accent="info" data-testid="yr-preview-clients" />
              <StatCard label="Properties" value={String(preview.propertiesToRoll)} caption="would roll" accent="info" data-testid="yr-preview-properties" />
              <StatCard label="Parcels" value={String(preview.parcelsToRoll)} caption={`${preview.parcelsToSkip} skipped`} accent="green" />
              <StatCard label="Invoice terms" value={String(preview.invoiceTermsToRoll)} caption="would roll" accent="hunter" />
              {!preview.scoped && (
                <>
                  <StatCard label="Assessors" value={String(preview.assessorsToRoll)} caption="advance year" accent="info" data-testid="yr-preview-assessors" />
                  <StatCard label="Collectors" value={String(preview.collectorsToRoll)} caption="advance year" accent="info" data-testid="yr-preview-collectors" />
                  <StatCard label="Jurisdictions" value={String(preview.jurisdictionsToRoll)} caption="new-year info" accent="amber" data-testid="yr-preview-jurisdictions" />
                </>
              )}
            </div>
          </>
        )}
      </div>

      {/* Confirmation */}
      <div style={{
        padding: 'var(--sp-5)',
        background: 'var(--color-status-amber-bg, var(--color-card))',
        border: '1px solid var(--color-status-amber)',
        borderRadius: 'var(--r-lg)',
        marginBottom: 'var(--sp-5)',
      }}>
        <h2 style={{ margin: '0 0 var(--sp-3)', fontSize: 'var(--fs-lg)', fontWeight: 'var(--fw-semibold)', color: 'var(--color-status-amber)' }}>Confirmation</h2>
        <p style={{ margin: '0 0 var(--sp-3)', color: 'var(--color-ink)', fontSize: 'var(--fs-md)', fontWeight: 'var(--fw-medium)' }}>
          {mode === 'scoped'
            ? `Scoped roll — only ${selectedClient ? 'the selected client' : selectedState ? `the selected state (${selectedState})` : 'the selected scope'} rolls. The organization year and shared government reference data are NOT advanced.`
            : `Org-wide roll — every client, property, parcel, invoice term and the government reference data advance from ${fromYear} to ${toYear}, and the organization year is advanced.`}
        </p>
        <p style={{ margin: '0 0 var(--sp-3)', color: 'var(--color-ink)', fontSize: 'var(--fs-md)' }}>
          Type <code style={{ background: 'var(--color-bg)', padding: '2px 6px', borderRadius: 'var(--r-sm)', fontFamily: 'var(--font-mono)', fontWeight: 'var(--fw-medium)', color: 'var(--color-ink)' }}>{requiredConfirm}</code> to enable the Run button.
        </p>
        <Input value={confirmText} onChange={e => setConfirmText(e.target.value)} placeholder={requiredConfirm} />
      </div>

      <footer style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--sp-2)', paddingTop: 'var(--sp-4)', borderTop: '1px solid var(--color-border)', marginBottom: 'var(--sp-6)' }}>
        <Button variant="secondary" onClick={() => navigate('/')} disabled={rollMutation.isPending}>Cancel</Button>
        <Button
          variant="primary"
          loading={rollMutation.isPending}
          disabled={!confirmMatches || !scopeChosen || rollMutation.isPending}
          onClick={() => rollMutation.mutate()}
        >
          Run year roll
        </Button>
      </footer>

      {/* History */}
      <section>
        <h2 style={{ margin: '0 0 var(--sp-3)', fontSize: 'var(--fs-lg)', fontWeight: 'var(--fw-semibold)', color: 'var(--color-ink)' }}>Roll history</h2>
        {historyQuery.isLoading && <p style={{ color: 'var(--color-ink-3)' }}>Loading history…</p>}
        {!historyQuery.isLoading && (historyQuery.data?.length ?? 0) === 0 && (
          <p style={{ color: 'var(--color-ink-3)', fontSize: 'var(--fs-md)' }}>No year rolls have been run yet.</p>
        )}
        {(historyQuery.data?.length ?? 0) > 0 && (
          <DataTable data={historyQuery.data ?? []} columns={historyColumns} />
        )}
      </section>
    </>
  );
}

function errorMessage(err: Error): string {
  return err instanceof ApiClientError && err.body && typeof err.body === 'object' && 'error' in err.body
    ? String((err.body as { error: unknown }).error)
    : err.message;
}

function YearRollComplete({ result, onClose }: { result: YearRollResult; onClose: () => void }) {
  return (
    <div>
      <header style={{ marginBottom: 'var(--sp-5)' }}>
        <h1 style={{ margin: 0, fontSize: 'var(--fs-3xl)', fontWeight: 'var(--fw-semibold)', color: 'var(--color-ink)' }}>Year roll complete</h1>
        <p style={{ margin: 'var(--sp-1) 0 0', color: 'var(--color-ink-3)', fontSize: 'var(--fs-md)' }}>
          {result.fromYear} to {result.toYear}. Roll ID: <code style={{ fontFamily: 'var(--font-mono)' }}>{result.rollId.slice(0, 8)}…</code>
        </p>
      </header>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 220px), 1fr))', gap: 'var(--sp-4)', marginBottom: 'var(--sp-6)' }}>
        <StatCard label="Clients" value={String(result.clientsRolled)} caption="rolled forward" accent="green" data-testid="yr-complete-clients" />
        <StatCard label="Properties" value={String(result.propsRolled)} caption="rolled forward" accent="green" data-testid="yr-complete-properties" />
        <StatCard label="Parcels rolled" value={String(result.parcelsRolled)} caption={`${result.parcelsSkipped} skipped`} accent="green" />
        <StatCard label="Invoice terms" value={String(result.termsRolled)} caption={result.itemsCreated ? `${result.itemsCreated} items auto-created` : 'no contingency items'} accent="hunter" />
        <StatCard label="Assessors" value={String(result.assessorsRolled ?? 0)} caption="advanced to new year" accent="info" data-testid="yr-complete-assessors" />
        <StatCard label="Collectors" value={String(result.collectorsRolled ?? 0)} caption="advanced to new year" accent="info" data-testid="yr-complete-collectors" />
      </div>

      <Button variant="primary" onClick={onClose}>Back to Dashboard</Button>
    </div>
  );
}
